export interface HttpErrorResponse<T = unknown> {
  status: number;
  data: T;
}

export class HttpError<T = unknown> extends Error {
  status: number;
  data: T;
  response: HttpErrorResponse<T>;
  code: string;

  constructor(message: string, status: number, data: T) {
    super(message);
    this.name = "HttpError";
    Object.setPrototypeOf(this, HttpError.prototype);
    this.status = status;
    this.data = data;
    this.response = { status, data };
    this.code = status >= 400 && status < 500 ? "ERR_BAD_REQUEST" : "ERR_BAD_RESPONSE";
  }
}

export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

async function parseResponseBody(response: globalThis.Response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function requestJson<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(url, options);
  const data = await parseResponseBody(response);

  if (!response.ok) {
    const message =
      data && typeof data === "object" && "message" in data
        ? String((data as { message?: unknown }).message)
        : `HTTP error ${response.status}`;

    throw new HttpError(message, response.status, data);
  }

  return data as T;
}

export function postJson<T>(
  url: string,
  body: unknown,
  headers: Record<string, string> = {}
): Promise<T> {
  return requestJson<T>(url, {
    method: "POST",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(body),
  });
}

export function getJson<T>(
  url: string,
  headers: Record<string, string> = {}
): Promise<T> {
  return requestJson<T>(url, {
    method: "GET",
    headers: {
      "Accept": "application/json",
      ...headers,
    },
  });
}

export function deleteJson<T>(
  url: string,
  body: unknown,
  headers: Record<string, string> = {}
): Promise<T> {
  return requestJson<T>(url, {
    method: "DELETE",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(body),
  });
}
